/**
 * Removes an empty, attribute-less `<div></div>` that a third-party
 * subscriptions/selling-plan app script injects as a direct child of every
 * `form.product-card` (alongside its own `name="product-id"` /
 * `name="section-id"` / `name="selling_plan"` hidden inputs) whenever a
 * product has no eligible selling plan to show. product-card.liquid never
 * renders this element itself — it only ever adds `name="id"` /
 * `name="quantity"` hidden inputs to that form — so this is purely cleanup
 * of markup injected after the fact by a script this theme doesn't control.
 *
 * Scoped as narrowly as possible so it can never touch anything this theme
 * actually renders: only a DIRECT CHILD of `form.product-card` with zero
 * attributes and zero content qualifies. Every div product-card.liquid
 * renders carries at least a class; the one legitimate class-less div in
 * that snippet (the swatch color swap) lives inside a `<button>`, several
 * levels deep — never a direct child of the form — so it's never a match.
 *
 * Uses a MutationObserver instead of polling, so cleanup fires right after
 * the app's own script actually touches the DOM rather than on a fixed
 * timer. It still never removes anything on first sight: any DIV that shows
 * up as a direct child of a product-card form is only ever a *candidate* —
 * actual removal is deferred and re-verified after a short delay, so if the
 * app is mid-way through populating that div for a product that *does* have
 * an eligible selling plan (e.g. appending it once, then filling it in
 * asynchronously a moment later), it's given time to do so before this ever
 * considers it empty. The observer keeps running for the life of the page,
 * so it also catches product cards that load later (recommendation
 * carousels, cart drawer re-renders, filtered/paginated collection content).
 */
(function () {
  var VERIFY_DELAY_MS = 1500;
  var scheduled = new WeakSet();

  function isEmptyCandidateDiv(el) {
    return (
      el.tagName === "DIV" &&
      el.parentElement != null &&
      el.parentElement.tagName === "FORM" &&
      el.parentElement.classList.contains("product-card") &&
      el.attributes.length === 0 &&
      el.childNodes.length === 0
    );
  }

  function isCandidateShape(el) {
    return (
      el.nodeType === 1 &&
      el.tagName === "DIV" &&
      el.parentElement != null &&
      el.parentElement.tagName === "FORM" &&
      el.parentElement.classList.contains("product-card")
    );
  }

  function scheduleVerify(el) {
    if (scheduled.has(el)) return;
    scheduled.add(el);

    setTimeout(function () {
      scheduled.delete(el);
      // Re-check at removal time, not at detection time — only remove if
      // it's STILL empty and attribute-less after the app has had a chance
      // to populate it.
      if (isEmptyCandidateDiv(el)) {
        el.remove();
      }
    }, VERIFY_DELAY_MS);
  }

  function scan(node) {
    if (node.nodeType !== 1) return;

    if (isCandidateShape(node)) {
      scheduleVerify(node);
    }

    // A whole product card (or a container holding one) can be inserted in
    // one go — e.g. a recommendation carousel or cart drawer re-render —
    // so also check for product-card forms nested inside what was added.
    var forms = node.matches && node.matches("form.product-card") ? [node] : [];
    if (node.querySelectorAll) {
      forms = forms.concat(Array.prototype.slice.call(node.querySelectorAll("form.product-card")));
    }
    forms.forEach(function (form) {
      Array.prototype.forEach.call(form.children, function (child) {
        if (isCandidateShape(child)) {
          scheduleVerify(child);
        }
      });
    });
  }

  function start() {
    // Catch anything already in the DOM at script start.
    scan(document.body);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(scan);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
